import { Pagination } from '@/Domain/Procedures/RepositoryModifiers/Pagination'
import { ProcedureFilters } from '@/Domain/Procedures/RepositoryModifiers/ProcedureFilters'
import { ProcedureOrdering } from '@/Domain/Procedures/RepositoryModifiers/ProcedureOrdering'
import {
  Procedure,
  ProcedureId,
  ProcedureNotFoundError,
  StandardProcedureFactory,
  ProcedureRepositoryError,
  UserProcedure,
  ProcedureBatches,
  UserProcedureRepository
} from '@/Domain/Procedures'
import { AccountId, AccountNotFoundError } from '@/Domain/Account'
import { ApiClient, Api500Error, Api404Error } from '@/Domain/ApiClient'

export class AxiosUserProcedureRepository implements UserProcedureRepository {
  private factory: StandardProcedureFactory
  private _apiClient: ApiClient

  constructor (apiClient: ApiClient, factory: StandardProcedureFactory) {
    this._apiClient = apiClient
    this.factory = factory
  }

  async byUser (
    accountId: AccountId,
    pagination: Pagination,
    filters: ProcedureFilters,
    // eslint-disable-next-line
    ordering: ProcedureOrdering
  ): Promise<UserProcedure[]> {
    const data = {
      page: pagination.page,
      itemsPerPage: pagination.itemsPerPage,
      ...filters
    }
    const url = '/sobree/api/procedures/offers/' + accountId.accountId()
    try {
      const response = await this._apiClient.makeGetRequest(url, data)
      const procedures: UserProcedure[] = []
      for (const item of response.data.data) {
        const procedure = this.factory.makeProcedure(item)
        const userProcedure = new UserProcedure(
          procedure,
          item.applicationStatus,
          new ProcedureBatches(item.batches)
        )
        procedures.push(userProcedure)
      }
      return procedures
    } catch (error) {
      if (error instanceof Api404Error) {
        return Promise.reject(new AccountNotFoundError('Account Not Found: ' + error.message))
      }
      return Promise.reject(this.transformError(error))
    }
  }

  async totalItemsByUserCount (accountId: AccountId, filters: ProcedureFilters): Promise<number> {
    const data = { ...filters }
    const url = '/sobree/api/procedures/offers/' + accountId.accountId() + '/count'
    try {
      const response = await this._apiClient.makeGetRequest(url, data)
      return parseInt(response.data.data.total, 10)
    } catch (error) {
      return Promise.reject(this.transformError(error))
    }
  }

  async byUserbyId (accountId: AccountId, procedureId: ProcedureId): Promise<Procedure> {
    const url = '/sobree/api/procedures/offers/' + accountId.accountId() + '/' + procedureId.id
    try {
      const response = await this._apiClient.makeGetRequest(url, {})
      const procedure = this.factory.makeProcedure(response.data.data)
      return procedure
    } catch (error) {
      return Promise.reject(this.transformError(error))
    }
  }

  private transformError (error: Error): Error {
    if (error instanceof Api500Error) {
      return new ProcedureRepositoryError('Server unavailable: ' + error.message)
    }
    if (error instanceof Api404Error) {
      return new ProcedureNotFoundError('Procedure not found: ' + error.message)
    }
    return error
  }
}
